/**
 * YMM Navigation Dropdown Component
 * Handles the interactive functionality for the Year/Make/Model filter dropdown
 * in the navigation header.
 */

class YMMNavDropdown {
  constructor(element) {
    this.dropdown = element;
    this.trigger = element.querySelector("[data-ymm-trigger]");
    this.panel = element.querySelector("[data-ymm-panel]");
    this.backdrop = element.querySelector("[data-ymm-backdrop]");
    this.closeBtn = element.querySelector("[data-ymm-close]");
    this.form = element.querySelector("[data-ymm-nav-form]");

    this.isOpen = false;
    this.isMobile = window.innerWidth < 990;

    this.init();
  }

  init() {
    this.bindEvents();
    this.checkEasySearchApp();
    this.setupAccessibility();
  }

  bindEvents() {
    // Trigger button
    if (this.trigger) {
      this.trigger.addEventListener("click", () => this.toggle());
    }

    // Close button (mobile)
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", () => this.close());
    }

    // Backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener("click", () => this.close());
    }

    // ESC key
    document.addEventListener("keydown", (e) => this.handleKeydown(e));

    // Click outside (desktop only)
    document.addEventListener("click", (e) => this.handleClickOutside(e));

    // Window resize
    window.addEventListener("resize", () => this.handleResize());

    // Form submission
    if (this.form) {
      this.form.addEventListener("submit", (e) => this.handleSubmit(e));
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;

    // Add active classes
    this.trigger?.classList.add("is-active");
    this.panel?.classList.add("is-open");
    this.backdrop?.classList.add("is-open");

    // Update ARIA
    this.trigger?.setAttribute("aria-expanded", "true");

    // Prevent body scroll on mobile
    if (this.isMobile) {
      document.body.style.overflow = "hidden";
    }

    // Focus management
    this.focusFirstInput();

    // Dispatch custom event
    this.dropdown.dispatchEvent(
      new CustomEvent("ymm-dropdown-opened", {
        bubbles: true,
        detail: { dropdown: this.dropdown },
      }),
    );
  }

  close() {
    this.isOpen = false;

    // Remove active classes
    this.trigger?.classList.remove("is-active");
    this.panel?.classList.remove("is-open");
    this.backdrop?.classList.remove("is-open");

    // Update ARIA
    this.trigger?.setAttribute("aria-expanded", "false");

    // Restore body scroll
    document.body.style.overflow = "";

    // Return focus to trigger
    this.trigger?.focus();

    // Dispatch custom event
    this.dropdown.dispatchEvent(
      new CustomEvent("ymm-dropdown-closed", {
        bubbles: true,
        detail: { dropdown: this.dropdown },
      }),
    );
  }

  handleKeydown(e) {
    if (!this.isOpen) return;

    switch (e.key) {
      case "Escape":
        this.close();
        e.preventDefault();
        break;
      case "Tab":
        this.handleTabKey(e);
        break;
    }
  }

  handleTabKey(e) {
    if (!this.panel) return;

    const focusableElements = this.panel.querySelectorAll(
      'button:not([disabled]), select:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  }

  handleClickOutside(e) {
    // Only on desktop
    if (window.innerWidth < 990) return;

    if (this.isOpen && !this.dropdown.contains(e.target)) {
      this.close();
    }
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 990;

    // Restore body scroll if resizing from mobile to desktop while open
    if (wasMobile && !this.isMobile && this.isOpen) {
      document.body.style.overflow = "";
    }

    // Apply mobile scroll lock if resizing from desktop to mobile while open
    if (!wasMobile && this.isMobile && this.isOpen) {
      document.body.style.overflow = "hidden";
    }
  }

  handleSubmit(e) {
    // Allow default form submission, but you can add custom logic here
    // For example, validation or analytics tracking
    console.log("YMM Nav: Form submitted");
  }

  focusFirstInput() {
    if (!this.panel) return;

    setTimeout(() => {
      const firstInput = this.panel.querySelector(
        "select:not([disabled]), input:not([disabled])",
      );
      if (firstInput) {
        firstInput.focus();
      }
    }, 100);
  }

  setupAccessibility() {
    // Ensure trigger has proper ARIA attributes
    if (this.trigger) {
      this.trigger.setAttribute("aria-expanded", "false");

      if (!this.trigger.hasAttribute("aria-controls")) {
        this.trigger.setAttribute("aria-controls", "ymm-nav-panel");
      }

      if (!this.trigger.hasAttribute("aria-label")) {
        this.trigger.setAttribute("aria-label", "Find parts by vehicle");
      }
    }

    // Ensure panel has proper role
    if (this.panel) {
      if (!this.panel.hasAttribute("role")) {
        this.panel.setAttribute("role", "dialog");
      }

      if (!this.panel.hasAttribute("aria-label")) {
        this.panel.setAttribute("aria-label", "Select your vehicle");
      }
    }
  }

  checkEasySearchApp() {
    // Check if EasySearch YMM app has loaded and injected content
    setTimeout(() => {
      const filterContainer = this.panel?.querySelector(
        ".section-filters-aeroflow-nav",
      );
      const easySearchWidget =
        filterContainer?.querySelector("[data-easysearch]") ||
        filterContainer?.querySelector(".easysearch-widget") ||
        filterContainer?.querySelector("form:not(.ymm-nav-form)");

      if (easySearchWidget) {
        console.log("YMM Nav: EasySearch app detected");
        this.dropdown.classList.add("has-easysearch");
        // EasySearch loaded, fallback form stays hidden
      } else {
        console.log("YMM Nav: Using fallback form");
        this.dropdown.classList.add("has-fallback");
        // Show the fallback form
        if (filterContainer) {
          filterContainer.classList.add("show-fallback");
        }
        this.initFallbackForm();
      }
    }, 1500);
  }

  initFallbackForm() {
    // Initialize fallback form functionality if EasySearch app didn't load
    // This would need to be implemented based on your data source
    console.log("YMM Nav: Fallback form initialized");

    // Example: You could fetch vehicle data from a custom API
    // or use product tags/metafields to build the filter options
  }

  destroy() {
    // Cleanup method for removing event listeners
    document.removeEventListener("keydown", (e) => this.handleKeydown(e));
    document.removeEventListener("click", (e) => this.handleClickOutside(e));
    window.removeEventListener("resize", () => this.handleResize());

    // Restore body scroll if it was locked
    if (this.isOpen) {
      document.body.style.overflow = "";
    }
  }
}

// Auto-initialize all YMM dropdowns on the page
document.addEventListener("DOMContentLoaded", () => {
  const dropdowns = document.querySelectorAll("[data-ymm-nav]");

  dropdowns.forEach((dropdown) => {
    new YMMNavDropdown(dropdown);
  });
});

// Export for potential module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = YMMNavDropdown;
}
